/**
 * adminRetentionHelpers.js — pure helpers for /admin/retention.
 *
 * No React, no API client, no UI deps — keeps the helpers Jest-
 * testable under the project's CRA/craco config (where the `@/`
 * webpack alias does not resolve in tests).
 *
 * Operator-locked classification rules (RC 1.47.3 Phase 1):
 *   GREEN  = success (errors=0 AND not a dry-run)
 *   YELLOW = dry-run preview (errors=0)
 *   RED    = errors > 0  (overrides dry-run)
 */

export function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year:   'numeric',
      month:  'short',
      day:    '2-digit',
      hour:   '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function durationMs(startIso, endIso) {
  if (!startIso || !endIso) return null;
  try {
    const a = new Date(startIso).getTime();
    const b = new Date(endIso).getTime();
    if (Number.isNaN(a) || Number.isNaN(b)) return null;
    return Math.max(0, b - a);
  } catch {
    return null;
  }
}

export function fmtDuration(ms) {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000)  return `${ms} ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(2)} s`;
  return `${(ms / 60000).toFixed(2)} min`;
}

// Returns { tone, label } — Icon is bound by the page component to
// keep this module dependency-free.
export function classifyRun(run) {
  const errors = Number(run?.errors ?? 0);
  if (Number.isFinite(errors) && errors > 0) {
    return { tone: 'red',    label: 'Error' };
  }
  if (run?.dry_run) {
    return { tone: 'yellow', label: 'Dry-run' };
  }
  return     { tone: 'green',  label: 'Success' };
}

export const TONE_CLASSES = {
  green:  'border-emerald-500/40 bg-emerald-500/10 text-emerald-200',
  yellow: 'border-amber-500/40   bg-amber-500/10   text-amber-200',
  red:    'border-rose-500/40    bg-rose-500/10    text-rose-200',
};

export const TONE_DOT = {
  green:  'bg-emerald-500',
  yellow: 'bg-amber-500',
  red:    'bg-rose-500',
};

export function shortenUuid(u) {
  if (!u) return '—';
  const s = String(u);
  if (s.length <= 13) return s;
  return `${s.slice(0, 8)}…${s.slice(-4)}`;
}
